import { useInView, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";

/**
 * Cycles through scene states while the scene is on screen and the tab is
 * visible. The server renders `initial` (the scene's most telling state);
 * reduced motion keeps it frozen there.
 */
export function useSceneLoop<T extends HTMLElement = HTMLDivElement>(
	durations: readonly number[],
	{ initial = 0, enabled = true }: { initial?: number; enabled?: boolean } = {},
) {
	const ref = useRef<T>(null);
	const inView = useInView(ref, { margin: "-10% 0px -10% 0px" });
	const reducedMotion = useReducedMotion();
	const [index, setIndex] = useState(initial);
	const [pageVisible, setPageVisible] = useState(true);

	useEffect(() => {
		const onChange = () => setPageVisible(!document.hidden);
		document.addEventListener("visibilitychange", onChange);
		return () => document.removeEventListener("visibilitychange", onChange);
	}, []);

	const playing = enabled && inView && pageVisible && !reducedMotion;

	useEffect(() => {
		if (!playing) return;
		const timer = window.setTimeout(
			() => setIndex((current) => (current + 1) % durations.length),
			durations[index],
		);
		return () => window.clearTimeout(timer);
	}, [playing, index, durations]);

	return { ref, index };
}
